<template>
  <div class="product-store-pricing-section">
    <div class="mb-4">
      <b-row class="align-items-center">
        <b-col md="4">
          <b-input
            v-model="storeFilter"
            size="sm"
            placeholder="Tìm kiếm theo tên kho..."
            @input="onFilterChange"
          />
        </b-col>
        <b-col md="8" class="text-right">
          <b-button
            variant="outline-primary"
            size="sm"
            class="mr-2"
            @click="openHistoryModal"
            :disabled="!selectedStoreForHistory"
          >
            <i class="fas fa-history mr-1"></i>
            Xem lịch sử
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            @click="showAddForm"
            :disabled="isAddingNew"
          >
            <i class="flaticon2-add-1 mr-1"></i>
            Thêm cấu hình
          </b-button>
        </b-col>
      </b-row>
    </div>

    <div v-if="loading" class="d-flex justify-content-center py-5">
      <div class="spinner spinner-primary"></div>
    </div>

    <div v-else>
      <b-table
        :fields="priceFields"
        :items="tableItems"
        bordered
        hover
        small
        :busy="loading"
        :tbody-tr-class="rowClass"
      >
        <template v-slot:table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <template v-slot:cell(store.name)="row">
          <div v-if="row.item.isNew">
            <b-form-select
              v-model="newConfig.storeId"
              :options="availableStores"
              size="sm"
              value-field="id"
              text-field="name"
              :state="validateNewConfig('storeId')"
            >
              <template v-slot:first>
                <b-form-select-option :value="null">
                  Chọn kho
                </b-form-select-option>
              </template>
            </b-form-select>
          </div>
          <div v-else-if="row.item.isEditing">
            <b-form-select
              v-model="editingConfig.storeId"
              :options="availableStoresForEdit(row.item)"
              size="sm"
              value-field="id"
              text-field="name"
            />
          </div>
          <div v-else>
            {{ row.item.store?.name || row.item.store?.shortName || '-' }}
          </div>
        </template>

        <template v-slot:cell(sellingPrice)="row">
          <div v-if="row.item.isNew">
            <b-input
              v-model="newConfig.sellingPrice"
              size="sm"
              placeholder="0"
              type="text"
              @input="formatPriceInput('sellingPrice', $event)"
              :state="validateNewConfig('price')"
              class="text-right"
            />
          </div>
          <div v-else-if="row.item.isEditing">
            <b-input
              v-model="editingConfig.sellingPrice"
              size="sm"
              type="text"
              @input="formatEditPriceInput('sellingPrice', $event)"
              class="text-right"
            />
          </div>
          <div v-else class="text-right">
            {{ formatCurrency(row.item.sellingPrice) }}
          </div>
        </template>

        <template v-slot:cell(wholesalePrice)="row">
          <div v-if="row.item.isNew">
            <b-input
              v-model="newConfig.wholesalePrice"
              size="sm"
              placeholder="0"
              type="text"
              @input="formatPriceInput('wholesalePrice', $event)"
              class="text-right"
            />
          </div>
          <div v-else-if="row.item.isEditing">
            <b-input
              v-model="editingConfig.wholesalePrice"
              size="sm"
              type="text"
              @input="formatEditPriceInput('wholesalePrice', $event)"
              class="text-right"
            />
          </div>
          <div v-else class="text-right">
            {{ formatCurrency(row.item.wholesalePrice) }}
          </div>
        </template>

        <template v-slot:cell(listedPrice)="row">
          <div v-if="row.item.isNew">
            <b-input
              v-model="newConfig.listedPrice"
              size="sm"
              placeholder="0"
              type="text"
              @input="formatPriceInput('listedPrice', $event)"
              class="text-right"
            />
          </div>
          <div v-else-if="row.item.isEditing">
            <b-input
              v-model="editingConfig.listedPrice"
              size="sm"
              type="text"
              @input="formatEditPriceInput('listedPrice', $event)"
              class="text-right"
            />
          </div>
          <div v-else class="text-right">
            {{ formatCurrency(row.item.listedPrice) }}
          </div>
        </template>

        <template v-slot:cell(updateChildProducts)="row">
          <div v-if="row.item.isNew" class="text-center">
            <b-form-checkbox
              v-model="newConfig.updateChildProducts"
              :disabled="!isParentProduct"
              v-b-tooltip.hover
              :title="
                isParentProduct
                  ? 'Cập nhật tất cả sản phẩm con với cùng giá'
                  : 'Chỉ khả dụng cho sản phẩm cha'
              "
            />
          </div>
          <div v-else-if="row.item.isEditing" class="text-center">
            <b-form-checkbox
              v-model="editingConfig.updateChildProducts"
              :disabled="!isParentProduct"
              v-b-tooltip.hover
              :title="
                isParentProduct
                  ? 'Cập nhật tất cả sản phẩm con với cùng giá'
                  : 'Chỉ khả dụng cho sản phẩm cha'
              "
            />
          </div>
          <div v-else class="text-center">
            <i
              v-if="row.item.updateChildProducts"
              class="fas fa-check text-success"
              v-b-tooltip.hover
              title="Cập nhật SP con được bật"
            ></i>
            <i v-else class="fas fa-times text-muted"></i>
          </div>
        </template>

        <template v-slot:cell(actions)="row">
          <div v-if="row.item.isNew" class="text-right">
            <b-button
              variant="success"
              size="sm"
              @click="saveNewConfig"
              :disabled="!isNewConfigValid || saving"
              class="mr-1"
            >
              <i class="flaticon2-checkmark"></i>
            </b-button>
            <b-button
              variant="secondary"
              size="sm"
              @click="cancelAddForm"
              :disabled="saving"
            >
              <i class="flaticon2-cross"></i>
            </b-button>
          </div>
          <div v-else-if="row.item.isEditing" class="text-right">
            <b-button
              variant="success"
              size="sm"
              @click="saveEdit(row.item)"
              :disabled="saving"
              class="mr-1"
            >
              <i class="flaticon2-checkmark"></i>
            </b-button>
            <b-button
              variant="secondary"
              size="sm"
              @click="cancelEdit(row.item)"
              :disabled="saving"
            >
              <i class="flaticon2-cross"></i>
            </b-button>
          </div>
          <div v-else class="text-right">
            <b-button
              variant="light-primary"
              size="sm"
              @click="startEdit(row.item)"
              class="mr-1"
            >
              <i class="flaticon2-pen"></i>
            </b-button>
            <b-button
              variant="light-danger"
              size="sm"
              @click="confirmDelete(row.item)"
            >
              <i class="flaticon2-rubbish-bin-delete-button"></i>
            </b-button>
          </div>
        </template>

        <template v-slot:empty>
          <div class="text-center py-4">
            <p class="text-muted">Chưa có cấu hình giá cho kho nào</p>
          </div>
        </template>
      </b-table>

      <div v-if="totalPages > 1" class="mt-3">
        <b-pagination-nav-custom
          :current-page="currentPage"
          :number-of-pages="totalPages"
          :total-page="totalPages"
          @page-change="handlePageChange"
        />
      </div>
    </div>

    <price-history-modal ref="priceHistoryModal" />
  </div>
</template>

<script>
import { ProductStorePricingService } from '../services/ProductStorePricingService';
import PriceHistoryModal from './modals/PriceHistoryModal.vue';
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue';
import {
  makeToastSuccess,
  makeToastFaile,
  convertPrice,
  unMaskMoney,
} from '@/utils/common';
import commonMixin from '@/view/mixins/api-common.mixins';
import Swal from 'sweetalert2';

export default {
  name: 'ProductStorePricingSection',

  components: {
    PriceHistoryModal,
    BPaginationNavCustom,
  },

  mixins: [commonMixin],

  props: {
    productId: {
      type: [Number, String],
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
    productRelationType: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      storePrices: [],
      stores: [],
      loading: false,
      saving: false,
      storeFilter: '',
      isAddingNew: false,
      newConfig: {
        storeId: null,
        sellingPrice: '',
        wholesalePrice: '',
        listedPrice: '',
        updateChildProducts: false,
      },
      editingConfig: {
        id: null,
        storeId: null,
        sellingPrice: '',
        wholesalePrice: '',
        listedPrice: '',
        updateChildProducts: false,
      },
      selectedStoreForHistory: null,
      currentPage: 1,
      perPage: 10,
      totalItems: 0,
      searchDebounce: null,
      priceFields: [
        { key: 'store.name', label: 'Kho', sortable: true },
        { key: 'sellingPrice', label: 'Giá bán lẻ', class: 'text-right' },
        { key: 'wholesalePrice', label: 'Giá bán buôn', class: 'text-right' },
        { key: 'listedPrice', label: 'Giá niêm yết', class: 'text-right' },
        {
          key: 'updateChildProducts',
          label: 'Cập nhật SP con',
          class: 'text-center',
          thStyle: 'width: 120px',
        },
        {
          key: 'actions',
          label: '',
          class: 'text-right',
          thStyle: 'width: 120px',
        },
      ],
    };
  },

  computed: {
    tableItems() {
      const items = [...this.storePrices];
      if (this.isAddingNew) {
        items.unshift({ isNew: true });
      }
      return items;
    },

    availableStores() {
      const usedStoreIds = this.storePrices.map((p) => p.storeId);
      return this.stores.filter((store) => !usedStoreIds.includes(store.id));
    },

    isNewConfigValid() {
      return (
        this.newConfig.storeId &&
        (this.newConfig.sellingPrice ||
          this.newConfig.wholesalePrice ||
          this.newConfig.listedPrice)
      );
    },

    isParentProduct() {
      return this.productRelationType === 'PARENT';
    },

    totalPages() {
      return Math.ceil(this.totalItems / this.perPage);
    },
  },

  watch: {
    active: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.productId) {
          this.fetchData();
        }
      },
    },

    productId: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.active) {
          this.fetchData();
        }
      },
    },
  },

  mounted() {
    if (this.productId && this.active) {
      this.fetchData();
    }
  },

  beforeDestroy() {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
  },

  methods: {
    async fetchData() {
      await Promise.all([this.fetchStorePrices(), this.fetchStoresList()]);
    },

    async fetchStorePrices() {
      if (!this.productId) return;

      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          limit: this.perPage,
        };

        if (this.storeFilter) {
          params.search = this.storeFilter;
        }

        const response = await ProductStorePricingService.getStorePrices(
          this.productId,
          params,
        );

        if (response.data) {
          this.storePrices = (response.data.items || []).map((item) => ({
            ...item,
            isEditing: false,
          }));

          if (response.data.pagination) {
            this.totalItems = response.data.pagination.total || 0;
            this.currentPage = response.data.pagination.page || 1;
          }

          if (this.storePrices.length > 0) {
            this.selectedStoreForHistory = this.storePrices[0];
          }
        }
      } catch (error) {
        makeToastFaile(error?.data?.message || 'Lỗi khi tải dữ liệu giá');
        this.storePrices = [];
        this.totalItems = 0;
      } finally {
        this.loading = false;
      }
    },

    async fetchStoresList() {
      try {
        const response = await this.fetchStores();
        if (response.status === 1 && response.data) {
          this.stores = response.data;
        }
      } catch (error) {
        console.error('Error fetching stores:', error);
      }
    },

    onFilterChange() {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
      }

      this.searchDebounce = setTimeout(() => {
        this.currentPage = 1;
        this.fetchStorePrices();
      }, 500);
    },

    handlePageChange(page) {
      this.currentPage = page;
      this.fetchStorePrices();
    },

    showAddForm() {
      this.isAddingNew = true;
      this.resetNewConfig();
    },

    cancelAddForm() {
      this.isAddingNew = false;
      this.resetNewConfig();
    },

    resetNewConfig() {
      this.newConfig = {
        storeId: null,
        sellingPrice: '',
        wholesalePrice: '',
        listedPrice: '',
        updateChildProducts: false,
      };
    },

    validateNewConfig(field) {
      if (field === 'storeId') {
        return this.newConfig.storeId ? true : null;
      }
      if (field === 'price') {
        return this.newConfig.sellingPrice ||
          this.newConfig.wholesalePrice ||
          this.newConfig.listedPrice
          ? true
          : null;
      }
      return null;
    },

    formatPriceInput(field, value) {
      let numericValue = value.replace(/[^\d]/g, '');
      if (numericValue) {
        this.newConfig[field] = parseInt(numericValue, 10).toLocaleString(
          'vi-VN',
        );
      } else {
        this.newConfig[field] = '';
      }
    },

    formatEditPriceInput(field, value) {
      let numericValue = value.replace(/[^\d]/g, '');
      if (numericValue) {
        this.editingConfig[field] = parseInt(numericValue, 10).toLocaleString(
          'vi-VN',
        );
      } else {
        this.editingConfig[field] = '';
      }
    },

    async saveNewConfig() {
      if (!this.isNewConfigValid) return;

      this.saving = true;
      try {
        const payload = {
          sellingPrice: this.newConfig.sellingPrice
            ? parseInt(unMaskMoney(this.newConfig.sellingPrice), 10)
            : undefined,
          wholesalePrice: this.newConfig.wholesalePrice
            ? parseInt(unMaskMoney(this.newConfig.wholesalePrice), 10)
            : undefined,
          listedPrice: this.newConfig.listedPrice
            ? parseInt(unMaskMoney(this.newConfig.listedPrice), 10)
            : undefined,
          updateChildProducts: this.isParentProduct
            ? this.newConfig.updateChildProducts
            : false,
        };

        const response = await ProductStorePricingService.setStorePrice(
          this.productId,
          this.newConfig.storeId,
          payload,
        );

        const successMessage =
          response.message || 'Thêm cấu hình giá thành công';
        const childrenUpdated = response.data?.childrenUpdated;
        const finalMessage = childrenUpdated
          ? `${successMessage} - ${childrenUpdated} sản phẩm con đã được cập nhật`
          : successMessage;

        makeToastSuccess(finalMessage);
        this.isAddingNew = false;
        this.resetNewConfig();
        this.currentPage = 1;
        await this.fetchStorePrices();
      } catch (error) {
        makeToastFaile(error?.data?.message || 'Lỗi khi thêm cấu hình giá');
      } finally {
        this.saving = false;
      }
    },

    startEdit(item) {
      this.storePrices.forEach((p) => (p.isEditing = false));

      item.isEditing = true;
      this.editingConfig = {
        id: item.id,
        storeId: item.storeId,
        sellingPrice: item.sellingPrice ? convertPrice(item.sellingPrice) : '',
        wholesalePrice: item.wholesalePrice
          ? convertPrice(item.wholesalePrice)
          : '',
        listedPrice: item.listedPrice ? convertPrice(item.listedPrice) : '',
        updateChildProducts: item.updateChildProducts || false,
      };
    },

    cancelEdit(item) {
      item.isEditing = false;
      this.editingConfig = {
        id: null,
        storeId: null,
        sellingPrice: '',
        wholesalePrice: '',
        listedPrice: '',
        updateChildProducts: false,
      };
    },

    async saveEdit(item) {
      this.saving = true;
      try {
        const payload = {
          sellingPrice: this.editingConfig.sellingPrice
            ? parseInt(unMaskMoney(this.editingConfig.sellingPrice), 10)
            : undefined,
          wholesalePrice: this.editingConfig.wholesalePrice
            ? parseInt(unMaskMoney(this.editingConfig.wholesalePrice), 10)
            : undefined,
          listedPrice: this.editingConfig.listedPrice
            ? parseInt(unMaskMoney(this.editingConfig.listedPrice), 10)
            : undefined,
          updateChildProducts: this.isParentProduct
            ? this.editingConfig.updateChildProducts
            : false,
        };

        const response = await ProductStorePricingService.setStorePrice(
          this.productId,
          item.storeId,
          payload,
        );

        const successMessage = response.message || 'Cập nhật giá thành công';
        const childrenUpdated = response.data?.childrenUpdated;
        const finalMessage = childrenUpdated
          ? `${successMessage} - ${childrenUpdated} sản phẩm con đã được cập nhật`
          : successMessage;

        makeToastSuccess(finalMessage);
        item.isEditing = false;
        await this.fetchStorePrices();
      } catch (error) {
        makeToastFaile(error?.data?.message || 'Lỗi khi cập nhật giá');
      } finally {
        this.saving = false;
      }
    },

    rowClass(item, type) {
      if (!item || type !== 'row') return;
      if (item.isNew) return 'table-light';
      if (item.isEditing) return 'table-warning';
    },

    confirmDelete(item) {
      Swal.fire({
        title: 'Xóa cấu hình giá!',
        text: 'Bạn có chắc muốn xóa cấu hình giá cho kho này không?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Xóa',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
      }).then(async (result) => {
        if (result.value) {
          await this.deleteConfig(item);
        }
      });
    },

    async deleteConfig(item) {
      this.saving = true;
      try {
        const response = await ProductStorePricingService.deleteStorePrice(
          this.productId,
          item.storeId,
        );

        makeToastSuccess(response.message || 'Xóa cấu hình giá thành công');
        this.currentPage = 1;
        await this.fetchStorePrices();
      } catch (error) {
        makeToastFaile(error?.data?.message || 'Lỗi khi xóa cấu hình giá');
      } finally {
        this.saving = false;
      }
    },

    openHistoryModal() {
      if (!this.selectedStoreForHistory) {
        makeToastFaile('Vui lòng chọn một kho để xem lịch sử');
        return;
      }

      this.$refs.priceHistoryModal.show(
        this.productId,
        this.selectedStoreForHistory.storeId,
        this.selectedStoreForHistory.store?.name ||
          this.selectedStoreForHistory.store?.shortName,
      );
    },

    availableStoresForEdit(item) {
      const usedStoreIds = this.storePrices
        .filter((p) => p.id !== item.id)
        .map((p) => p.storeId);
      return this.stores.filter((store) => !usedStoreIds.includes(store.id));
    },

    formatCurrency(value) {
      if (value === null || value === undefined) return '-';
      return convertPrice(value);
    },
  },
};
</script>

<style lang="scss" scoped>
.product-store-pricing-section {
  padding: 1rem;

  .bg-light {
    background-color: #f3f6f9 !important;
  }

  ::v-deep .btn-sm {
    min-width: 32px;
    height: 32px;
    padding: 0.375rem 0.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    i {
      margin: 0;
      padding: 0;
      line-height: 1;
      vertical-align: middle;
    }
  }
}
</style>
